import type { RefObject } from 'react';
import { useCallback, useMemo } from 'react';

import { dateFormat } from 'highcharts';

import watermarkBlackJpg from '@/assets/watermarks/watermark-black.jpg';
import watermarkBlackRaw from '@/assets/watermarks/watermark-black.svg?raw';
import watermarkColorJpg from '@/assets/watermarks/watermark-color.jpg';
import watermarkColorRaw from '@/assets/watermarks/watermark-color.svg?raw';
import type { WatermarkAttributes } from '@/components/Chart/types/highcharts';
import { useAppSelector } from '@/hooks/redux-hooks';
import sliceConfigFormat from '@/utils/sliceConfigFormat';

import type { Theme } from '@mui/material';
import type { ChartEventsOptions, PlotOptions, XAxisOptions } from 'highcharts';
import type Highcharts from 'highcharts/highstock';

// Vite serves image assets as URL paths in dev mode, but the Highcharts online export
// server can't reach local URLs. Pre-fetch and cache JPGs as data URIs at module load
// so they're ready when exportEvents.load() fires.
const toDataUri = (src: string): Promise<string> => {
  if (src.startsWith('data:')) return Promise.resolve(src);
  return fetch(src)
    .then(r => r.blob())
    .then(
      blob =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            resolve(reader.result as string);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    );
};

const jpgDataUriCache: Record<string, string | undefined> = {};
const prefetchJpgDataUris = async () => {
  const [blackResult, colorResult] = await Promise.allSettled([
    toDataUri(watermarkBlackJpg),
    toDataUri(watermarkColorJpg)
  ]);
  if (blackResult.status === 'fulfilled') {
    jpgDataUriCache['/watermark-black.svg'] = blackResult.value;
  }
  if (colorResult.status === 'fulfilled') {
    jpgDataUriCache['/watermark-color.svg'] = colorResult.value;
  }
};

// Avoid blocking module import in test environments where asset fetches can hang.
if (import.meta.env.MODE !== 'test' && !import.meta.env.VITEST) {
  void prefetchJpgDataUris();
}

export const CHART_GRID_COLOR = '#333';

export interface ChartDownloadProperties {
  format: string;
  fullScreen?: boolean;
  darkMode?: boolean;
  chartLocation: ChartParentComponent | null;
}

export interface ChartParentComponent {
  mainPageTitle: string;
  subPageTitle?: string;
  tabName?: string;
}

export interface ExtendedPlotOptions extends PlotOptions {
  custom?: object;
}

type WatermarkImageRef = RefObject<Highcharts.SVGElement | null>;
type WatermarkImageRefAccessor = WatermarkImageRef | (() => WatermarkImageRef);
const createYAxis = (color: string) => ({
  title: { text: undefined },
  maxPadding: 0.1,
  offset: 35,
  opposite: true,
  labels: {
    style: {
      color,
      fontSize: '10px'
    },
    formatter(): string {
      // @ts-expect-error: highcharts rules.
      return this.value.toFixed(2);
    }
  },
  gridLineColor: CHART_GRID_COLOR
});

interface WatermarkConfig {
  dataUri: string;
  aspectRatio: number;
}

// Match the JPG watermark resolution so rasterizers downsample (sharp) instead
// of upsample (blurry) when the SVG is rendered at chart plot width during export.
const WATERMARK_SVG_RENDER_WIDTH = 800;

const extractWatermarkConfig = (svgContent: string): WatermarkConfig => {
  const widthMatch = /width="(\d+)"/.exec(svgContent);
  const heightMatch = /height="(\d+)"/.exec(svgContent);
  const w = widthMatch ? Number.parseInt(widthMatch[1], 10) : 1;
  const h = heightMatch ? Number.parseInt(heightMatch[1], 10) : 1;
  const aspectRatio = w / h;

  const scaledH = Math.round(WATERMARK_SVG_RENDER_WIDTH / aspectRatio);
  const hiResSvg = svgContent
    .replace(`width="${w}"`, `width="${WATERMARK_SVG_RENDER_WIDTH}"`)
    .replace(`height="${h}"`, `height="${scaledH}"`);

  return {
    dataUri: `data:image/svg+xml;base64,${btoa(hiResSvg)}`,
    aspectRatio
  };
};

const watermarkConfigs: Record<string, WatermarkConfig> = {
  '/watermark-black.svg': extractWatermarkConfig(watermarkBlackRaw),
  '/watermark-color.svg': extractWatermarkConfig(watermarkColorRaw)
};

const defaultWatermarkConfig: WatermarkConfig = { dataUri: '', aspectRatio: 1 };

export const getWatermarkConfig = (name: string): WatermarkConfig =>
  watermarkConfigs[name] ?? defaultWatermarkConfig;

export const getStandardWatermarkAttributes = ({
  chart,
  aspectRatio,
  isUserBasic,
  isUserExpired
}: {
  chart: Highcharts.Chart;
  aspectRatio: number;
  isUserBasic: boolean;
  isUserExpired: boolean;
}): WatermarkAttributes => {
  const plotWidth = chart.plotWidth;
  const isRestricted = isUserBasic || isUserExpired;
  const w = isRestricted ? Math.round(plotWidth) : 100;
  const h = Math.round(w / aspectRatio);
  return isRestricted
    ? {
        width: w,
        height: h,
        x: -w / 2,
        y: -h / 2,
        zIndex: 0,
        align: 'center',
        verticalAlign: 'middle'
      }
    : {
        width: w,
        height: h,
        x: -w,
        y: -h + 12,
        zIndex: 0,
        align: 'right',
        verticalAlign: 'bottom'
      };
};

export const mergeChartEvents = (
  baseEvents: ChartEventsOptions | undefined,
  nextEvents: ChartEventsOptions
): ChartEventsOptions => {
  if (!baseEvents) return nextEvents;
  const merged: ChartEventsOptions = { ...baseEvents };
  const mergedEvents = merged as Record<
    string,
    ChartEventsOptions[keyof ChartEventsOptions] | undefined
  >;

  for (const eventName of Object.keys(nextEvents) as Array<keyof ChartEventsOptions>) {
    const nextHandler = nextEvents[eventName];
    if (!nextHandler) continue;
    const baseHandler = baseEvents[eventName];
    if (baseHandler) {
      const mergedHandler = function (this: Highcharts.Chart, ...args: unknown[]) {
        (baseHandler as (...args: unknown[]) => void).apply(this, args);
        (nextHandler as (...args: unknown[]) => void).apply(this, args);
      };
      mergedEvents[eventName] = mergedHandler as ChartEventsOptions[keyof ChartEventsOptions];
    } else {
      mergedEvents[eventName] = nextHandler;
    }
  }

  return merged;
};
export const useChartFunctions = () => {
  const { userConfig } = useAppSelector(state => state.userConfig);

  const userDateFormat = useMemo(() => {
    let configFormat = userConfig.dateFormat.value;
    const firstOccurrence = configFormat.indexOf('d');
    const lastOccurrence = configFormat.lastIndexOf('d');

    configFormat = sliceConfigFormat(firstOccurrence, configFormat, lastOccurrence);
    const delimiter = configFormat.includes('-') ? '-' : '/';
    const configParts = configFormat.split(delimiter);

    configFormat = configParts
      .map(config => {
        switch (config.toLowerCase()) {
          case 'mmm': {
            return '%b';
          }
          case 'mm': {
            return '%m';
          }
          case 'yy': {
            return '%y';
          }
          case 'yyyy': {
            return '%Y';
          }
          default:
            return null;
        }
      })
      .filter(Boolean)
      .join(delimiter);
    return configFormat;
  }, [userConfig]);
  const createEventOptions = (
    watermarkImage: WatermarkImageRefAccessor,
    getWatermarkAttributes: (chart: Highcharts.Chart, isExport?: boolean) => WatermarkAttributes,
    watermarkImageName: string
  ): { events: ChartEventsOptions; exportEvents: ChartEventsOptions } => {
    const { dataUri } = getWatermarkConfig(watermarkImageName);
    const resolveWatermarkRef = (): WatermarkImageRef =>
      typeof watermarkImage === 'function' ? watermarkImage() : watermarkImage;

    const applyWatermarkLayout = (image: Highcharts.SVGElement, attrs: WatermarkAttributes) => {
      image.attr({
        width: attrs.width,
        height: attrs.height,
        zIndex: attrs.zIndex
      });
      image.align(
        {
          align: attrs.align ?? 'right',
          verticalAlign: attrs.verticalAlign ?? 'bottom',
          x: attrs.x,
          y: attrs.y
        },
        false,
        'plotBox'
      );
    };

    return {
      events: {
        load() {
          const attrs = getWatermarkAttributes(this as unknown as Highcharts.Chart);
          const watermarkImageRef = resolveWatermarkRef();
          watermarkImageRef.current = this.renderer.image(dataUri).add();
          applyWatermarkLayout(watermarkImageRef.current, attrs);
        },
        redraw() {
          const watermarkImageRef = resolveWatermarkRef();
          if (watermarkImageRef.current) {
            const attrs = getWatermarkAttributes(this as unknown as Highcharts.Chart);
            applyWatermarkLayout(watermarkImageRef.current, attrs);
          }
        }
      },
      exportEvents: {
        load() {
          const chart = this as unknown as Highcharts.Chart;
          const attrs = getWatermarkAttributes(chart, true);

          const exportDataUri =
            chart.options.exporting?.type === 'image/jpeg'
              ? (jpgDataUriCache[watermarkImageName] ?? dataUri)
              : dataUri;

          const image = chart.renderer.image(exportDataUri).add();
          applyWatermarkLayout(image, attrs);

          for (const series of chart.series) {
            if (!series.visible) {
              // @ts-expect-error: highcharts rules.
              series.update({ showInLegend: false });
            }
          }
        }
      }
    };
  };

  const createNavigator = useCallback(
    (theme: Theme) => {
      return {
        xAxis: {
          gridLineColor: theme.palette.info.dark,
          labels: {
            style: {
              color: theme.palette.text.primary
            },
            formatter(): string {
              // @ts-expect-error: highcharts rules.
              const value = this.value;
              return dateFormat(userDateFormat, value);
            }
          }
        },
        enabled: true,
        adaptToUpdatedData: false,
        scrollbar: { enabled: true, barBackgroundColor: theme.palette.info.dark },
        outlineColor: theme.palette.info.dark,
        handles: {
          backgroundColor: theme.palette.background.default,
          borderColor: theme.palette.info.dark
        }
      };
    },
    [userDateFormat]
  );

  const createPlotOptions = useCallback(
    (): ExtendedPlotOptions => ({
      series: {
        animation: false,
        states: {
          inactive: { opacity: 1 },
          hover: { enabled: false }
        }
      },
      line: {
        states: {
          hover: { lineWidth: 3 }
        }
      }
    }),
    []
  );

  const createXAxis = useCallback(
    (color: string): XAxisOptions => {
      return {
        tickInterval: 30 * 24 * 3600 * 1000,
        ordinal: false,
        labels: {
          formatter(): string {
            const value = Number(this.value);
            return dateFormat(userDateFormat, value);
          },
          style: {
            color,
            fontSize: '10px'
          }
        },
        gridLineColor: CHART_GRID_COLOR,
        lineColor: CHART_GRID_COLOR,
        tickColor: CHART_GRID_COLOR
      };
    },
    [userDateFormat]
  );

  return {
    createNavigator,
    createPlotOptions,
    createYAxis,
    createXAxis,
    createEventOptions
  };
};

export default useChartFunctions;
